import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Workflow, Plus, Trash2, Star, Repeat, Clock, ArrowRight, LayoutTemplate } from 'lucide-react';
import { useAgentStudioNavigate } from '../lib/routes';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Tabs, type TabDef } from '../components/ui/Tabs';
import { CardSkeletonGrid, EmptyState } from '../components/ui/States';
import { ConfirmDialog } from '../components/ui/Modal';
import { FlowThumbnail } from '../components/FlowThumbnail';
import { FlowStatusBadge, OversightBadge } from '../components/StatusBadges';
import { flowsService } from '../services/flowsService';
import { templatesService } from '../services/templatesService';
import { newFlow } from '../lib/factories';
import { formatRelativeTime, cn } from '../lib/utils';
import { toast } from '../store/uiStore';
import type { Flow, FlowTemplate } from '../types';
import { AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions } from '../lib/permissions';

export function FlowsListPage() {
  const navigate = useAgentStudioNavigate();
  const { has } = useAgentStudioPermissions();
  const canManage = has(P.flowsManage);
  const canDelete = has(P.flowsDelete);
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<'flows' | 'templates'>(searchParams.get('view') === 'templates' ? 'templates' : 'flows');
  const [flows, setFlows] = useState<Flow[]>([]);
  const [templates, setTemplates] = useState<FlowTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [toDelete, setToDelete] = useState<Flow | null>(null);
  const [usingId, setUsingId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([flowsService.list(), templatesService.list()]).then(([f, t]) => {
      setFlows(f);
      setTemplates(t);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return flows.filter((f) => !normalizedQuery || (f.name ?? '').toLowerCase().includes(normalizedQuery));
  }, [flows, query]);

  async function createFlow() {
    const flow = await flowsService.save(newFlow());
    navigate(`/flows/${flow.id}`);
  }

  async function applyTemplate(tpl: FlowTemplate) {
    setUsingId(tpl.id);
    const flow = await templatesService.instantiate(tpl.id);
    if (!flow) {
      setUsingId(null);
      return;
    }
    const saved = await flowsService.save(flow);
    toast.success(`Cópia de "${tpl.name}" criada.`);
    navigate(`/flows/${saved.id}`);
  }

  async function toggleFavorite(e: React.MouseEvent, flow: Flow) {
    e.stopPropagation();
    const updated = { ...flow, favorite: !flow.favorite };
    setFlows((prev) => prev.map((f) => (f.id === flow.id ? updated : f)));
    await flowsService.save(updated);
  }

  async function confirmDelete() {
    if (!toDelete) return;
    await flowsService.remove(toDelete.id);
    setFlows((prev) => prev.filter((f) => f.id !== toDelete.id));
    toast.success(`Fluxo "${toDelete.name}" excluído.`);
  }

  const tabs: TabDef[] = [
    { id: 'flows', label: 'Meus fluxos', icon: Workflow, badge: flows.length || undefined },
    { id: 'templates', label: 'Templates', icon: LayoutTemplate, badge: templates.length || undefined },
  ];

  return (
    <>
      <PageHeader
        title="Fluxos"
        subtitle={view === 'flows' ? `${flows.length} fluxos · arraste agentes e nós no canvas` : 'Fluxos prontos para usar — crie uma cópia editável com um clique'}
        search={view === 'flows' ? query : undefined}
        onSearch={view === 'flows' ? setQuery : undefined}
        searchPlaceholder="Buscar fluxo…"
        actions={view === 'flows' && canManage ? <Button variant="primary" icon={Plus} onClick={createFlow}>Novo fluxo</Button> : undefined}
      />

      <div className="border-b border-line bg-white px-8">
        <Tabs tabs={tabs} active={view} onChange={(id) => setView(id as 'flows' | 'templates')} />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-8">
        {loading ? (
          <CardSkeletonGrid />
        ) : view === 'flows' ? (
          filtered.length === 0 ? (
            <EmptyState
              icon={Workflow}
              title={query ? 'Nenhum fluxo encontrado' : 'Nenhum fluxo ainda'}
              description={query ? 'Tente outro termo.' : 'Crie um fluxo do zero ou comece por um template.'}
              action={!query && canManage ? <Button variant="primary" icon={Plus} onClick={createFlow}>Novo fluxo</Button> : undefined}
            />
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((flow) => {
                const edges = Array.isArray(flow.edges) ? flow.edges : [];
                const hasLoop = edges.some((e) => e.isLoopBack);
                return (
                  <Card
                    key={flow.id || flow.name}
                    className={cn('group overflow-hidden transition-all hover:shadow-soft', canManage && 'cursor-pointer')}
                    onClick={canManage ? () => navigate(`/flows/${flow.id}`) : undefined}
                  >
                    <div className="relative border-b border-line bg-cloud p-3">
                      <FlowThumbnail flow={flow} className="h-28 w-full" />
                      {canManage && (
                        <button
                          onClick={(e) => toggleFavorite(e, flow)}
                          className={cn(
                            'absolute right-3 top-3 rounded-lg bg-white/80 p-1.5 backdrop-blur transition-colors hover:bg-white',
                            flow.favorite ? 'text-amber' : 'text-slate',
                          )}
                          aria-label="Favoritar"
                        >
                          <Star className={cn('h-4 w-4', flow.favorite && 'fill-amber')} />
                        </button>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold text-ink">{flow.name}</h3>
                        {canDelete && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setToDelete(flow);
                            }}
                            className="rounded-lg p-1 text-slate opacity-0 transition-opacity hover:bg-danger/10 hover:text-danger group-hover:opacity-100"
                            aria-label="Excluir"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-xs text-slate">{flow.description || 'Sem descrição.'}</p>

                      <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <FlowStatusBadge status={flow.status} />
                        <OversightBadge mode={flow.oversightMode} size="sm" />
                        {hasLoop && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-node-loop/20 bg-node-loop/10 px-2 py-0.5 text-xs font-medium text-node-loop">
                            <Repeat className="h-3 w-3" /> Loop
                          </span>
                        )}
                      </div>

                      <div className="mt-3 flex items-center gap-1.5 text-xs text-slate">
                        <Clock className="h-3.5 w-3.5" />
                        Atualizado {formatRelativeTime(flow.updatedAt)}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )
        ) : templates.length === 0 ? (
          <EmptyState icon={LayoutTemplate} title="Nenhum template disponível" description="Os templates são fluxos prontos para servir de ponto de partida." />
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {templates.map((tpl) => (
              <Card key={tpl.id} className="flex flex-col overflow-hidden">
                <div className="border-b border-line bg-cloud p-3">
                  <FlowThumbnail flow={tpl} className="h-28 w-full" />
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <div className="flex items-center gap-2">
                    <Badge tone="navy">{tpl.category}</Badge>
                    {tpl.hasLoop && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-node-loop/20 bg-node-loop/10 px-2 py-0.5 text-xs font-medium text-node-loop">
                        <Repeat className="h-3 w-3" /> Loop
                      </span>
                    )}
                  </div>
                  <h3 className="mt-2 text-sm font-semibold text-ink">{tpl.name}</h3>
                  <p className="mt-1 flex-1 text-xs text-slate">{tpl.description}</p>
                  <div className="mt-3">
                    <OversightBadge mode={tpl.oversightMode} size="sm" />
                  </div>
                  {canManage && (
                    <Button
                      variant="primary"
                      size="sm"
                      iconRight={ArrowRight}
                      className="mt-4 w-full"
                      loading={usingId === tpl.id}
                      onClick={() => applyTemplate(tpl)}
                    >
                      Usar template
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title="Excluir fluxo"
        message={`Tem certeza que deseja excluir "${toDelete?.name}"?`}
      />
    </>
  );
}
